import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { Placement } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { MagicRowSubtitle, resolveRowId } from '@iheartjane/menu';
import { Box, Flex, Typography } from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash-es/isEmpty';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';

import { useCustomImageRow, useFetchMultiPlacements } from '@jane/dm/internal';
import { buildSearchFilter, formatFeaturedCategory } from '@jane/search/util';
import { useGetStoreSpecialsPage } from '@jane/shared-ecomm/data-access';
import type { CustomImageAdProduct } from '@jane/shared/components';
import { BlockSkeleton, CustomImageAd } from '@jane/shared/components';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';
import { sleep } from '@jane/shared/util';

import {
  BrandSpecialsRow,
  DealBanner,
  FeaturedBanner,
  FilterAndSort,
  JaneGoldCallout,
  MenuHeader,
  MenuProducts,
  StoreSpecialsRow,
  getColumnCount,
} from '../../components';
import { CustomRowDisplay } from '../../components/lib/CustomRowDisplay';
import type { RootLoaderType } from '../../data-access';
import {
  searchOptions,
  useGetCart,
  useGetKiosk,
  useKioskUser,
} from '../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../hooks';
import {
  useBrandSpecials,
  useIdleContext,
  useInitialInteraction,
  useKioskJaneModeContext,
  useMenu,
  useMenuProductSearch,
  useSearchContext,
} from '../../providers';
import type { MenuCategory } from '../../types';
import {
  customCategoryLabel,
  trackHomeMenuTap,
  trackSignedOut,
} from '../../util';

export const Menu = () => {
  const customImageAdFlag = useFlag(FLAGS.ggCustomImageAds);
  const thematicRowContextFlag = useFlag(FLAGS.c3ThematicRowsContext);
  const categoriesRef = useRef<HTMLDivElement>(null);

  const {
    kiosk: initialKiosk,
    kioskId,
    store: initialStore,
    storeId,
  } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: kiosk } = useGetKiosk({
    initialData: initialKiosk,
    kioskId,
  });
  const { visibleTabs, bannerMessage, isDataLoaded } = useMenu();
  const { data: store, isSuccess } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { category = 'all', featuredCategory } = useParams<{
    category: MenuCategory;
    featuredCategory?: string;
  }>();
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1SpecialsData } = useGetStoreSpecialsPage(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    appMode: 'kiosk',
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const { data: kioskUser } = useKioskUser();
  const storeHasActiveSpecials = useMemo(() => {
    if (useV3) {
      const v3Specials =
        v3SpecialsQuery.data?.pages?.flatMap((page) => page.specials) ?? [];
      // v3 backend pre-filters to active specials; presence implies "active"
      return v3Specials.length > 0;
    }
    const specials =
      v1SpecialsData?.pages?.flatMap((page) => page.specials) || [];
    return !!specials.find((special) => !!special.enabled);
  }, [useV3, v1SpecialsData?.pages, v3SpecialsQuery.data?.pages]);

  const { hasActivated } = useIdleContext();
  const { brandSpecialFilter } = useBrandSpecials();

  const queryClient = useQueryClient();

  const scrollAnimation = async (initialDelay = 0) => {
    await sleep(initialDelay);
    categoriesRef.current?.scrollTo?.({ behavior: 'smooth', left: 128 });
    await sleep(1000);
    categoriesRef.current?.scrollTo?.({ behavior: 'smooth', left: -128 });
  };

  useEffect(() => {
    if (!hasActivated && !kiosk?.skip_landing_page) {
      scrollAnimation(1000);
    }
  }, [hasActivated, kiosk?.skip_landing_page]);

  const isFeatured = category === 'featured';
  const isSpecials = category === 'specials';
  const isGold = category === 'jane-gold';
  const isCategory = !isFeatured && !isSpecials && !isGold;

  const { resetSearchState, searchState, setSearchState } = useSearchContext();
  const defaultSortName = store?.optional_filters ? 'Featured' : null;

  const searchProps = useMenuProductSearch({
    category,
    columnCount: getColumnCount(),
    defaultSortName,
    enableMidGridRow: isCategory,
    featuredCategory,
    isEnabled: !isFeatured,
    store,
  });

  const { placements, isLoading: isLoadingMultiPlacements } =
    useFetchMultiPlacements({
      storeId,
      enabled: isFeatured,
    });

  const { searchResultFacets } = searchProps;

  const specialsProductCounts = useMemo(
    () => ({
      ...searchResultFacets['applicable_bundle_special_ids.qualify'],
      ...searchResultFacets['applicable_special_ids'],
    }),
    [searchResultFacets]
  );

  const isJaneMode = useKioskJaneModeContext();

  const location = useLocation();
  const navigate = useNavigate();
  useInitialInteraction({
    bizAppComms: !!bannerMessage?.enabled,
    categoryList: visibleTabs,
    isJaneMode,
    kioskComms: !!kiosk?.enable_comms_banner,
    onFirstTouch: kiosk?.skip_landing_page ? scrollAnimation : undefined,
    skippedLanding: !!kiosk?.skip_landing_page,
    specialsRowEnabled: !!store?.specials_row_configs?.enabled,
  });

  const { defaultSpacing, getKioskSpacing, isIpad } = useKioskSpacing();

  const { data: cart } = useGetCart();

  const useMenuRowType = useFlag(FLAGS.cnMenuRowTypeFe);
  const { featuredFilter } = formatFeaturedCategory(
    store,
    featuredCategory,
    useMenuRowType
  );
  const { staticFilters } = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
  });
  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters,
  });

  const imageAdRowProps = {
    pageCategory: isCategory ? category : undefined,
    searchAttributes: [
      'brand',
      'product_id',
      'image_urls',
      'category',
      'name',
      'aggregate_rating',
      'review_count',
    ],
    searchFilter,
    storeId,
  };

  const {
    instance: customImageRowInstance,
    isLoading: isFetchingCustomImageRow,
    customImageData,
  } = useCustomImageRow<CustomImageAdProduct>(imageAdRowProps);

  const onCategoryPress = useCallback(
    (category: MenuCategory) => {
      const productKindLabel = customCategoryLabel({
        kind: category,
        store,
      });
      const tabIndex = visibleTabs.findIndex((cat) => cat === category);
      trackHomeMenuTap({
        activeDeals: storeHasActiveSpecials,
        defaultTab: visibleTabs[0],
        isJaneMode,
        menuName: productKindLabel,
        optionSelected: category,
        tabPosition: tabIndex >= 0 ? tabIndex + 1 : null,
      });
      navigate(`/stores/${store.id}/menu/${category}`, {
        state: { resetSearch: true },
      });
    },
    [isJaneMode, navigate, store, storeHasActiveSpecials, visibleTabs]
  );

  const onStoreSwitchClick = useCallback(() => {
    trackHomeMenuTap({
      activeDeals: storeHasActiveSpecials,
      defaultTab: visibleTabs[0],
      isJaneMode,
      menuName: null,
      optionSelected: 'menu switcher',
      tabPosition: null,
    });
    navigate(`/stores/${store.id}/menu/${category}/switch-stores`);
  }, [
    category,
    isJaneMode,
    navigate,
    storeHasActiveSpecials,
    store,
    visibleTabs,
  ]);

  const onSearchIconClick = useCallback(() => {
    trackHomeMenuTap({
      activeDeals: storeHasActiveSpecials,
      defaultTab: visibleTabs[0],
      isJaneMode,
      menuName: null,
      optionSelected: 'search icon',
      tabPosition: null,
    });
    navigate(`/stores/${store.id}/menu/${category}/search`);
  }, [
    category,
    isJaneMode,
    navigate,
    storeHasActiveSpecials,
    store,
    visibleTabs,
  ]);

  const onLoginIconClick = useCallback(
    (optionSelected: 'login icon' | 'sign in prompt') => {
      trackHomeMenuTap({
        activeDeals: storeHasActiveSpecials,
        defaultTab: visibleTabs[0],
        isJaneMode,
        menuName: null,
        optionSelected,
        tabPosition: null,
      });
      navigate(`/stores/${store.id}/menu/${category}/login`);
    },
    [category, isJaneMode, navigate, storeHasActiveSpecials, store, visibleTabs]
  );

  const onLogoutIconClick = useCallback(() => {
    trackHomeMenuTap({
      activeDeals: storeHasActiveSpecials,
      defaultTab: visibleTabs[0],
      isJaneMode,
      menuName: null,
      optionSelected: 'logout icon',
      tabPosition: null,
    });
    trackSignedOut({ source: ['Menu'], store });
    navigate(`/stores/${store.id}/menu/${category}/logout`);
  }, [
    category,
    isJaneMode,
    navigate,
    storeHasActiveSpecials,
    store,
    visibleTabs,
  ]);

  const onCartButtonClick = useCallback(() => {
    trackHomeMenuTap({
      activeDeals: storeHasActiveSpecials,
      defaultTab: visibleTabs[0],
      isJaneMode,
      menuName: null,
      optionSelected: 'bag icon',
      tabPosition: null,
    });
    queryClient.invalidateQueries({ queryKey: ['cart'] });
    navigate(`/stores/${store.id}/cart`);
  }, [
    storeHasActiveSpecials,
    visibleTabs,
    isJaneMode,
    category,
    store,
    navigate,
  ]);

  useEffect(() => {
    if (location.state?.resetSearch) {
      resetSearchState();
      navigate(location, { replace: true, state: { resetSearch: false } });
    }
  }, [location, navigate, resetSearchState]);

  if (
    isDataLoaded &&
    !visibleTabs.includes(category) &&
    !['jane-gold', 'sale', 'specials'].includes(category)
  ) {
    return <Navigate to={`/stores/${store.id}/menu`} />;
  }

  // Is on featured or store doesn't have custom rows & is on default category
  const onDefaultCategory =
    isFeatured ||
    (!visibleTabs.includes('featured') && category === visibleTabs[0]);
  const showFeaturedBanner = kiosk?.enable_comms_banner && onDefaultCategory;
  const maybeShowImageAd =
    ((onDefaultCategory && bannerMessage?.banner_type !== 'image') ||
      (!onDefaultCategory && isCategory)) &&
    customImageAdFlag;

  return (
    <>
      <MenuHeader
        cart={cart}
        categoriesRef={categoriesRef}
        currentCategory={category}
        isJaneMode={isJaneMode}
        kioskName={kiosk?.name}
        signedIn={!!kioskUser}
        store={store}
        stores={kiosk?.stores}
        visibleTabs={visibleTabs}
        onCategoryPress={onCategoryPress}
        onStoreSwitchClick={onStoreSwitchClick}
        onSearchIconClick={onSearchIconClick}
        onCartButtonClick={onCartButtonClick}
        onLoginIconClick={onLoginIconClick}
        onLogoutIconClick={onLogoutIconClick}
      />
      {showFeaturedBanner && <FeaturedBanner />}
      {maybeShowImageAd && (
        <>
          {isFetchingCustomImageRow &&
            !customImageRowInstance?.products.length && (
              <Box width="100%" pt={32} px={48} style={{ aspectRatio: 21 / 7 }}>
                <BlockSkeleton height="100%" />
              </Box>
            )}
          {(customImageRowInstance?.products?.length ?? 0) > 0 && (
            <Box width="100%" pt={32} px={48}>
              <CustomImageAd
                isKiosk
                customImageData={customImageData}
                products={customImageRowInstance!.products.map((product) => ({
                  ...product.attributes,
                  clickHandler: () => product.click(),
                  impressionHandler: () => product.impress(),
                  pdpUrl: `${location.pathname}/products/${product.attributes.product_id}`,
                }))}
                onImageClick={(brandName) => {
                  setSearchState({
                    ...searchState,
                    filters: {
                      ...searchState.filters,
                      brand: [brandName],
                    },
                  });
                }}
                typography={Typography}
              />
            </Box>
          )}
        </>
      )}

      {onDefaultCategory && <DealBanner />}
      {isGold && (
        <Flex
          flexDirection="column"
          mb={getKioskSpacing(32, 16)}
          mt={defaultSpacing}
          gap={defaultSpacing}
        >
          <Box width="100%" px={getKioskSpacing(64, 48)}>
            <JaneGoldCallout variant="user" storeState={store.state} />
          </Box>
          <BrandSpecialsRow
            storeId={storeId}
            storeState={store.state}
            kioskUser={kioskUser}
          />
        </Flex>
      )}
      {isSpecials && (
        <Box mt={defaultSpacing} mb={getKioskSpacing(32, 16)}>
          <StoreSpecialsRow
            menuCategory="specials"
            specialsProductCounts={specialsProductCounts}
            store={store}
          />
        </Box>
      )}

      {isSuccess && !isFeatured && (
        <FilterAndSort
          condensedView={isGold}
          defaultSortName={defaultSortName}
          optionalFilters={store.optional_filters}
          searchProps={searchProps}
          hideFilterBar={isSpecials}
        />
      )}
      {isFeatured && !isLoadingMultiPlacements && placements && (
        <Flex
          flexDirection="column"
          mt={32}
          mb={getKioskSpacing(48, 32)}
          width="100%"
          gap={getKioskSpacing(48, 32)}
        >
          {placements.map((placement, index) => {
            if (placement.placement === Placement.Specials) {
              return (
                <StoreSpecialsRow
                  key={`specials-placement-${index}`}
                  menuCategory="featured"
                  specialsProductCounts={specialsProductCounts}
                  store={store}
                />
              );
            }

            if (placement.placement === Placement.BrandSpecials) {
              return (
                <BrandSpecialsRow
                  key={`brand-specials-placement-${index}`}
                  defaultLength={4}
                  isFeaturedPage
                  storeId={storeId}
                  storeState={store.state}
                  kioskUser={kioskUser}
                />
              );
            }

            return (
              <CustomRowDisplay
                key={`custom-row-placement-${index}`}
                columnCount={getColumnCount()}
                rowId={resolveRowId(
                  placement.rowId,
                  placement.title ?? undefined,
                  store.custom_row_settings
                )}
                rowInstance={placement}
                productCount={placement.totalProducts}
                store={store}
                hideViewAll={placement.placement === Placement.MenuDynamicRow}
                title={placement.title ?? ''}
                subtitle={
                  thematicRowContextFlag &&
                  placement.placement === Placement.MenuDynamicRow ? (
                    <MagicRowSubtitle iconSize={isIpad ? 'sm' : 'md'} />
                  ) : undefined
                }
              />
            );
          })}
        </Flex>
      )}
      {!isFeatured && (
        <MenuProducts
          category={category}
          searchProps={searchProps}
          showProductCount={!isGold}
          hideUnavailableMessage={
            isSpecials &&
            isEmpty(searchState?.bucketFilters) &&
            isEmpty(searchState?.filters) &&
            isEmpty(searchState?.rangeFilters) &&
            !searchState?.searchText
          }
        />
      )}
      <Outlet />
    </>
  );
};
